import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import { userService } from "../../../_services";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Download, BarChart3, List } from "lucide-react";

// Import modernized widgets
import BarWidget from '../BarWidget';
import ColumnPieWidget from '../ColumnPieWidget';
import MultiLineWidget from '../MultiLineWidget';
import ClaimWidget from '../Claim';
import TableWidget from '../TableWidget';
import MultiStackBarWidget from '../MultiStackBarWidget';

function AnalyticsPage() {
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return moment(date).format("YYYYMMDD");
    });
    
    const [endDate, setEndDate] = useState(() => {
        return moment(new Date()).format("YYYYMMDD");
    });

    const [graphData, setGraphData] = useState([]);
    const [redirectToLogin, setRedirectToLogin] = useState(false);
    const [legendValues, setLegendValues] = useState({});
    const [activeModes, setActiveModes] = useState({});
    const exportHandlersRef = useRef({});

    const handleLegendUpdate = useCallback((widgetId, value) => {
        setLegendValues(prev => ({ ...prev, [widgetId]: value }));
    }, []);

    const makeExportReady = useCallback((widgetId) => (fn) => {
        exportHandlersRef.current[widgetId] = fn;
    }, []);

    const handleModeChange = useCallback((widgetId, mode) => {
        setActiveModes(prev => ({ ...prev, [widgetId]: mode }));
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
            setRedirectToLogin(true);
            return;
        }

        const newDate = new Date();
        localStorage.setItem('Filter_Last_Update_Time', newDate);
        loadGraphData();
    }, []);

    const loadGraphData = () => {
        const APIBaseURL = '/api/ws';
        const startDateStr = startDate;
        const endDateStr = endDate;
        const filtertime = localStorage.getItem('Filter_Last_Update_Time') || '';
        const rxsClaimType = localStorage.getItem('Updated_HD_Type') || 'totalpaid';

        const widgets = [
            {
                title: 'Total Transactions',
                Subtitle: 'Total number of transactions for each day',
                url: `${APIBaseURL}/total_transactions_new`,
                RequestJson: JSON.stringify({
                    wname: "61",
                    wholesaler_id: "61",
                    start_date: startDateStr,
                    end_date: endDateStr,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime,
                    rxsClaimType
                }),
                RequestJson1: JSON.stringify(null),
                type: 'Bar',
                XlabelString: 'Date',
                YlabelString: '# of Transactions',
                id: 'WTotalTransaction'
            },
            {
                title: 'Transactions by Group Number',
                Subtitle: 'Number of transactions for each group number',
                url: `${APIBaseURL}/Transactions_By_Group_Number`,
                RequestJson: JSON.stringify({
                    wname: "61",
                    start_date: startDateStr,
                    end_date: endDateStr,
                    rcount: 30,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime,
                    rxsClaimType,
                    wholesaler_id: "61"
                }),
                RequestJson1: JSON.stringify(null),
                type: 'Bar',
                DataType: 'GroupNumber',
                XlabelString: 'Group Number',
                YlabelString: '# of Transactions',
                id: 'WGroupNumberTransaction'
            },
            {
                title: "Unique Member ID's",
                Subtitle: 'Number of unique members transacted on each day',
                url: `${APIBaseURL}/Unique_Members_Widget`,
                RequestJson: JSON.stringify({
                    wname: "61",
                    start_date: startDateStr,
                    end_date: endDateStr,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime,
                    rxsClaimType,
                    wholesaler_id: "61"
                }),
                RequestJson1: JSON.stringify(null),
                type: 'Bar',
                XlabelString: 'Date',
                YlabelString: "# of Unique Member ID's",
                id: 'WUniqueMembersWidget'
            },
            {
                title: 'Transactions by Drug',
                Subtitle: 'Number of transactions for each drug',
                url: `${APIBaseURL}/Drug_Wise_Transaction`,
                RequestJson: JSON.stringify({
                    wname: "61",
                    start_date: startDateStr,
                    end_date: endDateStr,
                    rcount: 30,
                    chart_type: "pielist",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime,
                    rxsClaimType,
                    wholesaler_id: "61"
                }),
                RequestJson1: JSON.stringify(null),
                type: 'ColumnPie',
                DataType: 'DrugName',
                XlabelString: 'Date',
                YlabelString: '# of Transactions',
                id: 'WDrugTransaction'
            }
        ];

        setGraphData(widgets);
    };

    // Widget types that support chart/table toggle
    const DUAL_MODE_TYPES = new Set(['Bar', 'MultiLine', 'MultiStackBar']);

    // Map widget type → readable chart label for tooltip
    const getChartLabel = (type) => {
        const map = { Bar: 'Bar', MultiLine: 'Line', MultiStackBar: 'Bar', ColumnPie: 'Pie' };
        return map[type] || 'Chart';
    };

    // Widgets expect a single `data` prop object — pass the full widget object
    const renderWidget = (widget, index) => {
        const commonProps = {
            data: widget,
            onLegendUpdate: handleLegendUpdate,
            onExportReady: makeExportReady(widget.id),
            mode: activeModes[widget.id] || 'chart'
        };
        const key = index;

        switch (widget.type) {
            case 'Bar':
                return <BarWidget key={key} {...commonProps} />;
            case 'ColumnPie':
                return <ColumnPieWidget key={key} {...commonProps} />;
            case 'MultiLine':
                return <MultiLineWidget key={key} {...commonProps} />;
            case 'Claim':
                return <ClaimWidget key={key} {...commonProps} />;
            case 'Table':
                return <TableWidget key={key} {...commonProps} />;
            case 'MultiStackBar':
                return <MultiStackBarWidget key={key} {...commonProps} />;
            default:
                return null;
        }
    };

    if (redirectToLogin) {
        return <Navigate to="/Login" />;
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Analytics</h1>
                    <p className="text-accent mt-1">View detailed analytics and insights</p>
                </div>
                <Badge variant="brand" className="uppercase shrink-0 self-start">
                    Beta
                </Badge>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {graphData.map((widget, index) => (
                    <Card key={`widget-${widget.id}-${index}`} className="col-span-12">
                        <CardHeader className="border-b">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <CardTitle className="text-lg font-semibold text-primary flex items-center gap-2 flex-wrap">
                                        {legendValues[widget.id] && (
                                            <span className="text-accent font-bold">
                                                {legendValues[widget.id]}
                                            </span>
                                        )}
                                        {widget.title}
                                    </CardTitle>
                                    {widget.Subtitle && (
                                        <CardDescription className="text-sm text-accent mt-1">
                                            {widget.Subtitle}
                                        </CardDescription>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {/* Export button — before toggle */}
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 text-primary border-primary/20 hover:bg-muted"
                                                    onClick={() => exportHandlersRef.current[widget.id]?.()}
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Download Report</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    {/* Chart / List toggle — only for dual-mode widgets */}
                                    {DUAL_MODE_TYPES.has(widget.type) && (
                                        <TooltipProvider>
                                            <div className="flex items-center rounded-md bg-muted p-1 gap-0.5">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className={`h-7 w-7 transition-colors ${(activeModes[widget.id] || 'chart') === 'chart' ? 'bg-accent text-white hover:bg-accent hover:text-white' : 'text-primary hover:bg-transparent hover:text-primary'}`}
                                                            onClick={() => handleModeChange(widget.id, 'chart')}
                                                        >
                                                            <BarChart3 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{getChartLabel(widget.type)}</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className={`h-7 w-7 transition-colors ${(activeModes[widget.id] || 'chart') === 'table' ? 'bg-accent text-white hover:bg-accent hover:text-white' : 'text-primary hover:bg-transparent hover:text-primary'}`}
                                                            onClick={() => handleModeChange(widget.id, 'table')}
                                                        >
                                                            <List className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>List</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 sm:p-6">
                            {renderWidget(widget, index)}
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

export default AnalyticsPage;
